import { useState, useCallback } from 'react';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { UnifiedMessagingService } from '@/services/api/unified-messaging.service';
import type { UnifiedMessage } from '@/types/unified-messaging';

interface UseMessageInputProps {
  conversationId: string | null;
  isOnline: boolean;
  queueOfflineMessage: (messageData: {
    content: string;
    messageType: string;
    messageData?: Record<string, unknown>;
    replyToId?: string;
  }) => void;
  addMessageToUI: (message: UnifiedMessage) => void;
  setError: (error: string | null) => void;
}

export function useMessageInput({
  conversationId,
  isOnline,
  queueOfflineMessage,
  addMessageToUI,
  setError
}: UseMessageInputProps) {
  const [messageInput, setMessageInput] = useState('');
  const [replyTo, setReplyTo] = useState<UnifiedMessage | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const { uploadMultiplePhotos, uploading, uploadProgress } = usePhotoUpload();

  const handlePhotoSelect = useCallback((files: FileList | null) => {
    if (!files) return;
    
    const newFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024 // 10MB limit
    );
    
    if (newFiles.length === 0) return;
    
    setSelectedPhotos(prev => [...prev, ...newFiles]);
    
    // Create preview URLs
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreviewUrls(prev => [...prev, e.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const removePhoto = useCallback((index: number) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearPhotos = useCallback(() => {
    setSelectedPhotos([]);
    setPhotoPreviewUrls([]);
  }, []);

  const handleSendMessage = useCallback(async (
    content: string,
    messageType?: string,
    messageDataParam?: Record<string, unknown>,
    replyToId?: string
  ) => {
    if (!content.trim() && !messageDataParam) return;

    // Prevent duplicate sends
    if (sending) {
      return;
    }

    // If offline, queue the message
    if (!isOnline) {
      queueOfflineMessage({
        content,
        messageType: messageType || 'text',
        messageData: messageDataParam,
        replyToId
      });

      // Clear input immediately for better UX
      setMessageInput('');
      setReplyTo(null);
      return;
    }

    setSending(true);

    try {
      const messageData = {
        conversation_id: conversationId!,
        content,
        message_type: (messageType || 'text') as any,
        message_data: messageDataParam,
        reply_to_id: replyToId
      };

      const response = await UnifiedMessagingService.getInstance().sendUnifiedMessage(messageData);

      if (response.success) {
        // Add message to UI immediately for optimistic update
        const optimisticMessage = response.data;
        addMessageToUI(optimisticMessage);

        // Clear input and reply
        setMessageInput('');
        setReplyTo(null);
      } else {
        console.error('❌ Failed to send message:', response.error);
        setError(response.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('❌ Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  }, [conversationId, isOnline, sending, queueOfflineMessage, addMessageToUI, setError]);

  const handleSendPhotos = useCallback(async () => {
    if (selectedPhotos.length === 0) return;

    try {
      // Upload photos
      const photoUrls = await uploadMultiplePhotos(selectedPhotos);
      
      if (photoUrls.length > 0) {
        // Send photo message
        await handleSendMessage(
          messageInput,
          'photo',
          { image_urls: photoUrls },
          replyTo?.id
        );
        
        // Clear photos and input
        clearPhotos();
        setMessageInput('');
        setReplyTo(null);
      }
    } catch (error) {
      console.error('📸 Error sending photos:', error);
    }
  }, [selectedPhotos, messageInput, replyTo, uploadMultiplePhotos, handleSendMessage, clearPhotos]);

  return {
    messageInput,
    setMessageInput,
    replyTo,
    setReplyTo,
    selectedPhotos,
    photoPreviewUrls,
    sending,
    uploading,
    uploadProgress,
    handlePhotoSelect,
    removePhoto,
    clearPhotos,
    handleSendMessage,
    handleSendPhotos,
    uploadMultiplePhotos,
  };
}

